import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAbortError, starchartApiService } from '../services/starchartApi';
import { starchartRankHistoryApiService } from '../services/starchartRankHistoryApi';
import { serverListApiService, type ServerListEntry } from '../services/serverListApi';
import { partitionCaseVariants } from '../utils/starchart';
import type {
  ModpackShareSlice,
  PopularityWindow,
  ServerAggregate,
  StarchartCatalogEntry,
  StarchartDbInfoResponse,
  StarchartLiveStatus,
  StarchartServedResolution,
  StarchartServerRow,
  StarchartTimePoint,
} from '../types/starchart';

/** Enough recent history to label a current player count for a server the socket cannot see. */
const ARCHIVED_LOOKBACK_MS = 30 * 60_000;

/** Top slices shown individually in the distribution ring; the rest collapse into "Other". */
export const DISTRIBUTION_SLICE_LIMIT = 6;

interface NetworkDataState {
  serverIds: string[];
  catalog: Map<string, ServerListEntry>;
  /** False when the PlayCDU server list could not be fetched — see `isArchived` below. */
  catalogLoaded: boolean;
  /**
   * Keyed by the server id **exactly** as the API spells it, not lowercased.
   * `PROJINF.playcdu.co` and `projinf.playcdu.co` are two distinct tracked series that appear in
   * the same ranking list at very different positions (#51 with an index of 6 versus #1 with
   * 232), so folding their case together silently shows one server's numbers under the other's
   * name. Only the joins to the server list and the status socket lowercase, because those are
   * different systems that happen to spell the same host differently.
   */
  rankings: Map<PopularityWindow, Map<string, { index: number; rank: number }>>;
  sparklines: Map<string, StarchartTimePoint[]>;
  fallbackCounts: Map<string, number>;
  rankDeltas: Map<string, number>;
  dbInfo: StarchartDbInfoResponse | null;
  loadedAt: number | null;
}

const EMPTY_STATE: NetworkDataState = {
  serverIds: [],
  catalog: new Map(),
  catalogLoaded: false,
  rankings: new Map(),
  sparklines: new Map(),
  fallbackCounts: new Map(),
  rankDeltas: new Map(),
  dbInfo: null,
  loadedAt: null,
};

const toCatalogEntry = (entry: ServerListEntry): StarchartCatalogEntry => ({
  modpackTitle: entry.modpackTitle,
  modpackVersion: entry.modpackVersion,
  modpackAuthor: entry.modpackAuthor,
  modpackLauncher: entry.modpackLauncher,
  modpackIndexId: entry.modpackIndexId,
  minecraftVersion: entry.minecraftVersion,
  thumbnailUrl: entry.thumbnailUrl,
  regionName: entry.regionName,
  clusterName: entry.clusterName,
  clusterParentServerId: entry.clusterParentServerId,
  isServerOnline: entry.isServerOnline,
  onlinePlayerCount: entry.onlinePlayerCount,
  maxPlayerCount: entry.maxPlayerCount,
});

/**
 * Index the PlayCDU server list by every FQDN it mentions.
 *
 * A cluster parent carries regional children under `clusterChildren`, and StarChart tracks those
 * children individually. Indexing only the parents would make every regional server look
 * archived — which is what the standalone site did, since it matched against a flat config list.
 */
const indexCatalog = (entries: ServerListEntry[]): Map<string, ServerListEntry> => {
  const map = new Map<string, ServerListEntry>();
  for (const entry of entries) {
    map.set(entry.serverAddress.toLowerCase(), entry);
    for (const child of entry.clusterChildren ?? []) {
      // Children inherit the parent's modpack identity but keep their own region and counts.
      map.set(child.serverAddress.toLowerCase(), {
        ...entry,
        serverAddress: child.serverAddress,
        regionName: child.regionName,
        isServerOnline: child.isServerOnline,
        onlinePlayerCount: child.onlinePlayerCount,
        maxPlayerCount: child.maxPlayerCount,
      });
    }
  }
  return map;
};

export interface UseStarchartNetworkResult {
  rows: StarchartServerRow[];
  distribution: ModpackShareSlice[];
  totalLivePlayers: number;
  activeServerCount: number;
  activeModpackCount: number;
  dbInfo: StarchartDbInfoResponse | null;
  loading: boolean;
  error: string | null;
  loadedAt: number | null;
  refresh: () => Promise<void>;
}

/**
 * Everything the StarChart overview needs, in four requests rather than sixty.
 *
 * The standalone site called a per-server timeseries endpoint once for every server — 58
 * requests, each pulling 24 hours of raw samples — purely to read the last value as a "current
 * players" number. Here current counts come from the live socket for free, and the one
 * `/stats/aggregate` call covers every sparkline at once.
 */
export function useStarchartNetwork(
  live: Map<string, StarchartLiveStatus>,
  popularityWindow: PopularityWindow,
  sparklineStart: Date,
  sparklineEnd: Date,
  sparklineResolution: StarchartServedResolution,
): UseStarchartNetworkResult {
  const [data, setData] = useState<NetworkDataState>(EMPTY_STATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(
    async (force = false) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const { signal } = controller;

      try {
        setError(null);

        const [serverIds, catalogEntries, rankings, dbInfo] = await Promise.all([
          starchartApiService.getServers({ signal, force }),
          serverListApiService.getServerList().catch(() => [] as ServerListEntry[]),
          starchartApiService.getRankings(popularityWindow, { signal, force }),
          starchartApiService.getDbInfo({ signal, force }).catch(() => null),
        ]);

        if (signal.aborted) return;

        const catalog = indexCatalog(catalogEntries);

        const rankingMap = new Map<string, { index: number; rank: number }>();
        rankings.ranked_list.forEach((item, i) => {
          rankingMap.set(item.server_id, { index: item.popularity_index, rank: i + 1 });
        });

        setData((prev) => ({
          ...prev,
          serverIds,
          catalog,
          catalogLoaded: catalogEntries.length > 0,
          rankings: new Map(prev.rankings).set(popularityWindow, rankingMap),
          dbInfo,
          loadedAt: Date.now(),
        }));
        setLoading(false);

        // Everything below is enrichment: the page is already usable, so a failure here must
        // never surface as a page-level error.
        const untracked = serverIds.filter((id) => !live.has(id.toLowerCase()));

        const [aggregate, fallback, movement] = await Promise.allSettled([
          starchartApiService.getAggregate(
            serverIds,
            sparklineStart,
            sparklineEnd,
            sparklineResolution,
            { signal },
          ),
          untracked.length > 0
            ? starchartApiService.getAggregate(
                untracked,
                new Date(Date.now() - ARCHIVED_LOOKBACK_MS),
                new Date(),
                'raw',
                { signal },
              )
            : Promise.resolve([] as ServerAggregate[]),
          starchartRankHistoryApiService.getRankMovement(popularityWindow, 24 * 3600, signal),
        ]);

        if (signal.aborted) return;

        const sparklines = new Map<string, StarchartTimePoint[]>();
        if (aggregate.status === 'fulfilled') {
          for (const entry of aggregate.value) {
            sparklines.set(entry.server_id, entry.data);
          }
        }

        const fallbackCounts = new Map<string, number>();
        if (fallback.status === 'fulfilled') {
          for (const entry of fallback.value) {
            const last = entry.data[entry.data.length - 1];
            if (last) fallbackCounts.set(entry.server_id, last.player_count);
          }
        }

        // Rank movement is optional: it only exists once argus-backend has been collecting.
        const rankDeltas = new Map<string, number>();
        if (movement.status === 'fulfilled') {
          for (const entry of movement.value.entries) {
            if (entry.rank_position === null || entry.previous_rank_position === null) continue;
            // Positive means the server climbed, which reads better than "rank went down".
            rankDeltas.set(entry.server_id, entry.previous_rank_position - entry.rank_position);
          }
        }

        setData((prev) => ({ ...prev, sparklines, fallbackCounts, rankDeltas }));
      } catch (err) {
        if (isAbortError(err) || signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load StarChart network data');
        setLoading(false);
      }
    },
    [popularityWindow, sparklineStart, sparklineEnd, sparklineResolution, live],
  );

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
    // `live` changes with every socket frame; re-running the whole load on that would be a
    // request storm. It is read inside `load` only to decide which servers need a fallback
    // count, which is fine to compute from whatever the socket had at load time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popularityWindow, sparklineStart, sparklineEnd, sparklineResolution]);

  const refresh = useCallback(async () => {
    starchartApiService.clearCache();
    setLoading(true);
    await load(true);
  }, [load]);

  const rows = useMemo<StarchartServerRow[]>(() => {
    const weights = new Map<string, number>();
    for (const id of data.serverIds) {
      // Weight by observed activity so the busier of two case variants stays canonical.
      const history = data.sparklines.get(id);
      const ranking = data.rankings.get(popularityWindow)?.get(id);
      weights.set(id, (history?.length ?? 0) + (ranking?.index ?? 0));
    }

    const { shadowed } = partitionCaseVariants(data.serverIds, (id) => weights.get(id) ?? 0);
    const shadowedSet = new Set(shadowed);

    const known = new Set(data.serverIds.map((id) => id.toLowerCase()));
    const untrackedFqdns = Array.from(data.catalog.keys()).filter((fqdn) => !known.has(fqdn));

    const buildRow = (apiServerId: string, isUntracked: boolean): StarchartServerRow => {
      // Exact for anything from the popularity API; lowercased only to reach the other systems.
      const fqdn = apiServerId.toLowerCase();
      const entry = data.catalog.get(fqdn);
      const liveStatus = live.get(fqdn) ?? null;
      const history = data.sparklines.get(apiServerId) ?? [];

      const popularity: StarchartServerRow['popularity'] = {};
      const rank: StarchartServerRow['rank'] = {};
      for (const [window, map] of data.rankings) {
        const hit = map.get(apiServerId);
        if (hit) {
          popularity[window] = hit.index;
          rank[window] = hit.rank;
        }
      }

      const rankDelta: StarchartServerRow['rankDelta'] = {};
      const delta = data.rankDeltas.get(apiServerId);
      if (delta !== undefined) rankDelta[popularityWindow] = delta;

      const historyLatest = history.length > 0 ? history[history.length - 1].player_count : null;
      const latestPlayers =
        liveStatus && !liveStatus.isOffline
          ? liveStatus.playerCount
          : (data.fallbackCounts.get(apiServerId) ?? historyLatest ?? 0);

      // Null rather than 0 when there is no history at all: an unknown peak and a peak of nobody
      // are different claims, and showing "0" next to 16 live players reads as a contradiction.
      let peakPlayers: number | null = null;
      for (const point of history) {
        if (peakPlayers === null || point.player_count > peakPlayers) {
          peakPlayers = point.player_count;
        }
      }

      return {
        fqdn,
        apiServerId,
        displayName: entry?.modpackTitle || apiServerId,
        catalog: entry ? toCatalogEntry(entry) : null,
        live: liveStatus,
        popularity,
        rank,
        rankDelta,
        history,
        latestPlayers,
        peakPlayers,
        // Only call a server archived when we actually have a catalogue to miss from. If the
        // PlayCDU server list is unreachable, marking all 58 servers archived would empty the
        // default view entirely — the data is fine, only the labelling is unknown.
        isArchived: data.catalogLoaded && !entry,
        isUntracked,
        isCaseShadowed: shadowedSet.has(apiServerId),
      };
    };

    return [
      ...data.serverIds.map((id) => buildRow(id, false)),
      // Servers PlayCDU runs that StarChart has never seen: shown, but with no history.
      ...untrackedFqdns.map((fqdn) => buildRow(fqdn, true)),
    ];
  }, [data, live, popularityWindow]);

  const distribution = useMemo<ModpackShareSlice[]>(() => {
    const byModpack = new Map<string, ModpackShareSlice>();

    for (const row of rows) {
      if (row.latestPlayers <= 0) continue;
      const key = row.catalog?.modpackTitle ?? row.fqdn;
      const slice = byModpack.get(key);
      if (slice) {
        slice.players += row.latestPlayers;
        slice.servers.push(row);
      } else {
        byModpack.set(key, {
          key,
          label: row.catalog?.modpackTitle ?? row.fqdn,
          players: row.latestPlayers,
          share: 0,
          thumbnailUrl: row.catalog?.thumbnailUrl ?? null,
          servers: [row],
        });
      }
    }

    const slices = Array.from(byModpack.values()).sort((a, b) => b.players - a.players);
    const total = slices.reduce((sum, slice) => sum + slice.players, 0);
    for (const slice of slices) {
      slice.share = total > 0 ? slice.players / total : 0;
      slice.servers.sort((a, b) => b.latestPlayers - a.latestPlayers);
    }
    return slices;
  }, [rows]);

  const totalLivePlayers = useMemo(
    () => rows.reduce((sum, row) => sum + row.latestPlayers, 0),
    [rows],
  );

  return {
    rows,
    distribution,
    totalLivePlayers,
    activeServerCount: rows.filter((row) => row.latestPlayers > 0).length,
    activeModpackCount: distribution.length,
    dbInfo: data.dbInfo,
    loading,
    error,
    loadedAt: data.loadedAt,
    refresh,
  };
}
